import React, { useReducer, useEffect, useContext } from 'react';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Menu, MenuItem } from '@mui/material';
import { Link } from 'react-router-dom';
import { PermissionsContext } from '../../UIContainer/PermissionsWrapper';
import { StorageAccountMenuProps } from '../Model/StorageAccountProps';
import { storageAccountService } from '../StorageAccountService';
import {
  MenuType,
  StorageAccountMenuItems,
} from '../Model/StorageAccountModel';

const menuItems: StorageAccountMenuItems[] = [
  {
    id: 'edit',
    label: 'Edit',
    getLink: (name: string) => `/storageaccount/${name}/edit`,
    validationFunc: MenuType.Edit,
  },
  {
    id: 'delete',
    label: 'Delete',
    validationFunc: MenuType.Delete,
  },
];

enum ActionTypes {
  OPEN_MENU = 'OPEN_MENU',
  CLOSE_MENU = 'CLOSE_MENU',
  SET_DELETE_PERMISSION = 'SET_DELETE_PERMISSION',
}

type StateType = {
  anchor: HTMLButtonElement | null;
  menuOpen: boolean;
  canDelete: boolean;
};

type ActionType =
  | { type: ActionTypes.OPEN_MENU; payload: HTMLButtonElement }
  | { type: ActionTypes.CLOSE_MENU }
  | { type: ActionTypes.SET_DELETE_PERMISSION; payload: boolean };

const reducer = (state: StateType, action: ActionType): StateType => {
  switch (action.type) {
    case ActionTypes.OPEN_MENU:
      return { ...state, menuOpen: true, anchor: action.payload };
    case ActionTypes.CLOSE_MENU:
      return { ...state, menuOpen: false, anchor: null };
    case ActionTypes.SET_DELETE_PERMISSION:
      return { ...state, canDelete: action.payload };
    default:
      return state;
  }
};

export default function StorageAccountMenu({
  Name,
  DeleteFunction,
}: Readonly<StorageAccountMenuProps>) {
  const permissions = useContext(PermissionsContext);

  const [state, dispatch] = useReducer(reducer, {
    anchor: null,
    menuOpen: false,
    canDelete: false,
  });

  useEffect(() => {
    const checkDeletePermission = async () => {
      const result = await storageAccountService.canDeleteStorageAccount(Name);
      dispatch({ type: ActionTypes.SET_DELETE_PERMISSION, payload: result });
    };
    checkDeletePermission();
  }, [Name]);

  const hasStorageAccountWriteAccess = () =>
    Boolean(permissions['StorageAccountWriter']);

  const executeValidationMethod = (type: 'edit' | 'delete'): boolean => {
    const validations: Record<'edit' | 'delete', () => boolean> = {
      delete: () => hasStorageAccountWriteAccess() && state.canDelete,
      edit: () => hasStorageAccountWriteAccess(),
    };
    return validations[type]();
  };

  return (
    <div>
      <IconButton
        aria-label="More"
        aria-haspopup="true"
        onClick={(e) =>
          dispatch({ type: ActionTypes.OPEN_MENU, payload: e.currentTarget })
        }
        size="large"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="storage-account-menu"
        anchorEl={state.anchor}
        open={state.menuOpen}
        onClose={() => dispatch({ type: ActionTypes.CLOSE_MENU })}
      >
        {menuItems
          .filter((menu) => executeValidationMethod(menu.validationFunc))
          .map((menu) => (
            <MenuItem
              key={menu.id}
              component={menu.getLink ? Link : 'div'}
              to={menu.getLink ? menu.getLink(Name) : undefined}
              onClick={menu.getLink ? undefined : () => DeleteFunction(Name)}
            >
              {menu.label}
            </MenuItem>
          ))}
      </Menu>
    </div>
  );
}
